import * as React from 'react'

import { cleanupTemporaryResources } from '@/utils/temp-resource-cleanup'

import { PPTX_MIME, shouldOfferResourceDownload } from '../constants'
import type { ResourceContainerProps } from '../types'

import type { ResourceType } from '@/types/resource.type'

export function useContentLoadEffect(
  contentType: string | undefined,
  resource: ResourceType,
  setIsLoading: ResourceContainerProps['setIsLoading']
) {
  const handleContentLoadSuccess = React.useCallback(async () => {
    try {
      if (resource.uuid && resource.uuidCopy) {
        await cleanupTemporaryResources(resource.uuid, resource.uuidCopy, resource.fileName)
      }
    } catch {
      /**/
    }
  }, [resource.uuid, resource.uuidCopy, resource.fileName])

  React.useEffect(() => {
    if (contentType?.includes('image')) {
      const handleImageLoad = () => {
        setIsLoading?.(false)
        handleContentLoadSuccess()
      }
      const img = new window.Image()
      img.onload = handleImageLoad
      img.src = resource.url || '/placeholder.svg'
    } else if (contentType?.includes('video')) {
      const handleVideoLoad = () => {
        setIsLoading?.(false)
        handleContentLoadSuccess()
      }
      const timer = setTimeout(handleVideoLoad, 3000)
      const checkVideoElement = () => {
        const videoElement = document.querySelector('video')
        if (videoElement) {
          const onVideoCanPlay = () => {
            clearTimeout(timer)
            handleVideoLoad()
          }
          videoElement.addEventListener('canplay', onVideoCanPlay, { once: true })
          videoElement.addEventListener('loadeddata', onVideoCanPlay, { once: true })
          videoElement.addEventListener('error', () => {
            clearTimeout(timer)
            handleVideoLoad()
          })
        }
      }
      setTimeout(checkVideoElement, 100)
      return () => clearTimeout(timer)
    } else if (contentType?.includes('yaml') || contentType?.includes('x-python')) {
      const timer = setTimeout(() => {
        setIsLoading?.(false)
        handleContentLoadSuccess()
      }, 100)
      return () => clearTimeout(timer)
    } else if (contentType?.includes('text')) {
      const timer = setTimeout(() => {
        setIsLoading?.(false)
        handleContentLoadSuccess()
      }, 100)
      return () => clearTimeout(timer)
    } else if (contentType?.includes('pdf')) {
      const handlePDFLoad = () => setIsLoading?.(false)
      const timer = setTimeout(handlePDFLoad, 3000)
      const checkIframeLoad = () => {
        const iframe = document.querySelector('iframe')
        if (iframe) {
          const onIframeLoad = () => {
            clearTimeout(timer)
            handlePDFLoad()
          }
          iframe.addEventListener('load', onIframeLoad, { once: true })
          iframe.addEventListener('error', () => {
            clearTimeout(timer)
            handlePDFLoad()
          })
        }
      }
      setTimeout(checkIframeLoad, 200)
      return () => clearTimeout(timer)
    } else if (contentType?.includes(PPTX_MIME) || contentType?.endsWith('.pptx')) {
      setIsLoading?.(false)
    } else if (contentType?.includes('zip') || contentType?.endsWith('.zip')) {
      setIsLoading?.(false)
    } else if (shouldOfferResourceDownload(contentType, resource.fileName)) {
      setIsLoading?.(false)
    } else {
      setIsLoading?.(false)
    }
  }, [
    contentType,
    resource.url,
    resource.uuid,
    resource.uuidCopy,
    resource.fileName,
    handleContentLoadSuccess,
    setIsLoading,
  ])

  return handleContentLoadSuccess
}
